import { useContext, useEffect } from 'react';
import { Achievements, AchievementType, ProfileContext } from '../smartsend/lib/context/ProfileContext';
import { useCallback } from 'react';
import { useAccountLinkingApi } from '~/lib/api/account-linking/use-account-linking-api';
import { useRuleManagerApi } from '~/lib/api/rulemanager/use-rulemanager-api';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import useExternalSearch from '../smartsend/components/smartsend/client/useExternalSearch';
import { useMeApi } from '~/lib/api/me/use-me-api';
import getExternalSearchQueryParams from '../smartsend/components/smartsend/client/GetExternalSearchQueryParams';
import { ExternalSearch, ExternalSearchBooked } from '../smartsend/lib/consts/ListyConsts';
import { useP2GQuery } from '@parcel2go/npm-p2g-app';
import { useAuth } from 'react-oidc-context';
import { GetSocketSessionId } from '../smartsend/lib/helpers/AuthHelper';
import { SignupFormResponse } from '../smartsend/components/onboarding/SignupFormResponse';
import { SignupForm } from '../smartsend/components/onboarding/SignupForm';
import { EventSections } from '../smartsend/lib/events/EventLogger';
import { useAchievements } from '~/hooks/use-achievements';

const useProfile = () => {
  const { user } = useAuth();
  const { dispatch, state } = useContext(ProfileContext);
  const { getintegrationsummary } = useAccountLinkingApi();
  const { createDefaultCustomer } = useSmartSendApi();
  const { getRules } = useRuleManagerApi();
  const { getExternalSearchQueryMarketplaceOrderCount, searchExternalSearchAndPollyFillItems } = useExternalSearch();
  const { addAchievements } = useAchievements();
  const { searchAddresses } = useMeApi();

  const { response: postRegistrationForm } = useP2GQuery<SignupFormResponse[]>({
    session: GetSocketSessionId(user?.profile),
    query: 'ui_smartsend_registration_form',
    enabled: !state.loading && state.signupIntegrations === undefined && state.numberOfParcelsPerWeek === undefined
  });

  useEffect(() => {
    if (postRegistrationForm?.length) {
      const parsed: SignupForm = JSON.parse(postRegistrationForm[0].FormObject);
      const keysFromSignupFormInterface: string[] = [
        'NumberOfParcelsSent',
        'BusinessSender',
        'NameOfBusiness',
        'Platforms',
        'ShippingProviders'
      ];
      if (parsed && Object.keys(parsed).every((k) => keysFromSignupFormInterface.includes(k))) {
        const integrations: string[] = [];

        if (parsed.Platforms['Not On The High Street']) {
          integrations.push('noths');
        }

        if (parsed.Platforms.Etsy) {
          integrations.push('etsy');
        }

        if (parsed.Platforms.eBay) {
          integrations.push('ebay');
        }

        if (parsed.Platforms.Shopify) {
          integrations.push('shopify');
        }

        if (parsed.Platforms.Amazon) {
          integrations.push('amazon');
        }

        if (parsed.Platforms.WooCommerce) {
          integrations.push('woocommerce');
        }

        if (parsed.Platforms.Wix) {
          integrations.push('wix');
        }

        if (integrations.length > 0 || parsed.NumberOfParcelsSent) {
          dispatch({
            type: 'USER-SET-POST-REGISTRATION-FORM-DATA',
            payload: { numberOfParcels: Number(parsed.NumberOfParcelsSent), integrations: integrations }
          });
        }
      }
    }
  }, [postRegistrationForm, dispatch]);

  const initialise = useCallback(() => {
    const load = async () => {
      const updated = Object.fromEntries(
        Achievements.map((key) => [key, state.achievements.find((a) => a.key === key)?.value ?? false])
      );

      if (!state.override) {
        const hasSendFirstOrder =
          updated['SmartSendActionFirstParcelSent'] ||
          (await getExternalSearchQueryMarketplaceOrderCount(ExternalSearchBooked)) > 0;

        for (const item in updated) {
          // Quick Return if they've sent before
          if (hasSendFirstOrder) {
            switch (item as AchievementType) {
              case 'SmartSendActionFirstParcelSent': {
                updated[item] = true;
                break;
              }
            }
            continue;
          }

          // Get DB
          if (updated[item] === false) {
            switch (item as AchievementType) {
              case 'SmartSendActionCollectionAddressAdded': {
                const res = await searchAddresses(1, 0);
                updated[item] = res.success && res.result ? res.result.addresses.length > 0 : false;

                // Get the latest order from the order process and set that as the default return address;
                if (res.success && res.result?.addresses && res.result.addresses.length === 0) {
                  const filters = getExternalSearchQueryParams('DEFAULT', ExternalSearch);
                  const al = await searchExternalSearchAndPollyFillItems(1, 1, filters, '');
                  if (al?.items?.length) {
                    await createDefaultCustomer({ ...al.items[0].Sender, AccountName: 'Default' });
                  }
                }

                break;
              }

              case 'SmartSendActionAccountLinked': {
                const res = await getintegrationsummary();
                updated[item] =
                  res.success && res.result ? res.result.filter((i) => i.accounts.length > 0).length > 0 : false;
                break;
              }

              case 'SmartSendActionFirstParcelSent': {
                updated[item] = hasSendFirstOrder;
                break;
              }

              case 'SmartSendActionRuleAdded': {
                const res = await getRules();
                updated[item] = res?.result?.length > 0;
                break;
              }
            }
          }
        }
      }

      // Db Persist
      const completedKeys = Object.keys(updated).filter((k) => updated[k as AchievementType] === true);
      if (completedKeys.length) {
        await addAchievements([EventSections.SmartSendSetup], completedKeys);
      }

      dispatch({
        type: 'USER-SET-ACHIEVEMENTS',
        payload: {
          achievements: updated,
          loading: false
        }
      });

      return {
        ...state,
        achievements: Object.keys(updated).map((k) => ({
          key: k as AchievementType,
          value: updated[k as AchievementType],
          hide: false
        })),
        loading: false
      };
    };

    return load();
  }, [
    createDefaultCustomer,
    dispatch,
    getExternalSearchQueryMarketplaceOrderCount,
    getRules,
    getintegrationsummary,
    searchAddresses,
    searchExternalSearchAndPollyFillItems,
    state.achievements,
    state.override,
    addAchievements
  ]);

  const hide = useCallback(
    (keys: AchievementType[]) => {
      dispatch({
        type: 'USER-HIDE-ACHIEVEMENTS',
        payload: keys
      });
    },
    [dispatch]
  );

  const markAsDone = useCallback(
    async (
      keys: AchievementType[],
      outstandingActionsToAdd: string[],
      outstandingActionsToRemove: string[],
      meta: { [key: string]: any }
    ) => {
      dispatch({
        type: 'USER-MARK-AS-DONE',
        payload: { achievements: keys, outstandingActionsToAdd, outstandingActionsToRemove, meta: meta }
      });
      await addAchievements([EventSections.SmartSendSetup], keys);
    },
    [dispatch, addAchievements]
  );

  const addOutstandingActions = useCallback(
    (key: string[]) => {
      dispatch({
        type: 'USER-ADD-ACTIONS-OUTSTANDING',
        payload: key
      });
    },
    [dispatch]
  );

  const removeOutstandingActions = useCallback(
    (key: string[]) => {
      dispatch({
        type: 'USER-REMOVE-ACTIONS-OUTSTANDING',
        payload: key
      });
    },
    [dispatch]
  );

  const setOutstandingActions = useCallback(
    (key: string[]) => {
      dispatch({
        type: 'USER-SET-ACTIONS-OUTSTANDING',
        payload: key
      });
    },
    [dispatch]
  );

  const skip = useCallback(() => {
    dispatch({ type: 'USER-SKIP-ONBOARDING' });
  }, [dispatch]);

  const endTestSession = useCallback(() => {
    dispatch({ type: 'END-TEST-SESSION' });
  }, [dispatch]);

  const reset = useCallback(
    (done?: AchievementType[]) => {
      dispatch({ type: 'TESTING-SET-STATE', payload: { achievements: done ?? [] } });
    },
    [dispatch]
  );

  const refresh = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  return {
    data: state,
    skip,
    reset,
    endTestSession,
    hide,
    refresh,
    addOutstandingActions,
    setOutstandingActions,
    removeOutstandingActions,
    markAsDone,
    initialise
  } as const;
};

export default useProfile;
